import { useEffect, useMemo, useState } from 'react';
import { CompareOptions, TopListBase, useSeriesColorGenerator } from '@/components';
import {
  type BarChartData,
  type BarChartPropsBase,
  type BarClickEvent,
  DEFAULT_TOPLIST_BARS_LIMIT,
  type ModeOption,
} from '@/core';
import { type BasePanelProps, useCompareMode, useDataQueryResult, usePanelKey } from '@/plugin-system';
import { useVizPanelNotice } from '@/plugin-system/components/VizPanelContextProvider';
import { useAppInteractionLevel } from '@/plugin-system/runtime';
import { MODE_TOGGLE_HEIGHT, ModeToggle } from './ModeToggle';
import { calculatePercentages, linkComparisonSeries, sortTopListSeriesData } from './utils';

/**
 * Types for the bar chart that are only used in the `Viz` variant.
 *
 * @see {@link BarChartPropsBase} for types defined in the schema because they are also shared with
 * the `DashboardPanel` variant.
 */
interface BarChartPropsVizOnly {
  /**
   * Show a ToggleButtonGroup control to switch between value/percentage modes.
   */
  showModeToggle?: boolean;
  /**
   * Callback fired when the mode value changes.
   * If provided, the mode toggle will be in a controlled state with the 'mode' prop.
   */
  onModeChange?: (mode?: ModeOption) => void;
  /**
   * Controls whether or not the bar chart will calculate percentage values.
   *   - 'all': format numbers and perform calculations to determine percentage values.
   *   - 'format-only': only format numbers, no additional calculations done. Use this when calculations are precalculated
   *     in the data source.
   * @default: 'all'
   */
  modeProcessing?: 'format-only' | 'all';
  /**
   * The label for the comparison period displayed in the tooltip during compare mode (e.g., '1 day prior')
   */
  comparisonPeriodLabel?: string;
  /**
   * Callback fired when a bar is clicked.
   */
  onBarClick?: (e: BarClickEvent) => void;
}

export interface BarChartPanelProps extends BasePanelProps, BarChartPropsBase, BarChartPropsVizOnly {
  // Extend values from `BarChartPropsBase` below, as needed, to add in jsdoc to help developers.
}

export function BarChartPanel(props: BarChartPanelProps) {
  const {
    unit,
    sort,
    mode: initialMode,
    overrides,
    width,
    height,
    showModeToggle,
    modeProcessing = 'all',
    comparisonPeriodLabel,
    thresholds,
    limit: explicitLimit,
    onBarClick,
  } = props;

  const limit = explicitLimit ?? DEFAULT_TOPLIST_BARS_LIMIT;

  const [internalMode, setInternalMode] = useState(initialMode);
  const isInternalMode = showModeToggle && !props.onModeChange;
  const mode = isInternalMode ? internalMode : initialMode;
  const onModeChange = isInternalMode ? setInternalMode : props.onModeChange;

  const colorGenerator = useSeriesColorGenerator();
  const contentDimensions = width && height ? { width, height } : undefined;

  const { queryResults } = useDataQueryResult({
    dataTypeFilter: 'SingleValue',
  });

  const { barChartData, dataMax, hiddenBarsCount, isCompareMode } = useMemo(() => {
    let isCompareMode = false;
    const barChartData: BarChartData[] = [];
    let dataMax = Number.MIN_SAFE_INTEGER;
    for (const result of queryResults) {
      // Skip queries that are still loading or don't have data
      if (result.isLoading || result.isFetching || result.data === undefined) continue;

      for (const data of result.data.values) {
        // If compareTo exists (indicating the index of the series to compare this value to),
        // set the value of this series to the comparisonValue of that index.
        if (data.config?.compareTo !== undefined) {
          if (isCompareMode === false) {
            isCompareMode = true;
          }
          linkComparisonSeries(barChartData, data);
        }
        // If compareTo does not exist, just add the current series to BarChartData
        else {
          const series = {
            value: data.value ?? null,
            label: data.formattedName || data.name,
          };
          if (data.value !== null && data.value > dataMax) {
            dataMax = data.value;
          }
          barChartData.push(series);
        }
      }
    }

    const sortedBarChartData = sortTopListSeriesData(barChartData, sort);
    const limitedBarChartData = sortedBarChartData.slice(0, limit);
    const hiddenBarsCount = sortedBarChartData.length - limitedBarChartData.length;

    if (modeProcessing === 'all' && mode === 'percentage') {
      return {
        barChartData: calculatePercentages(limitedBarChartData),
        dataMax,
        hiddenBarsCount,
        isCompareMode,
      };
    } else {
      return {
        barChartData: limitedBarChartData,
        dataMax,
        hiddenBarsCount,
        isCompareMode,
      };
    }
  }, [queryResults, sort, mode, modeProcessing, limit]);

  const appInteractionLevel = useAppInteractionLevel();
  const { setPanelNotice: setHiddenBarsNotice, deletePanelNotice: deleteHiddenBarsNotice } =
    useVizPanelNotice('hidden_bars');

  useEffect(() => {
    if (hiddenBarsCount > 0) {
      const isUserEditable = appInteractionLevel === 'editable-view' || appInteractionLevel === 'editable-edit';

      const additionalEditMessage = ' Increase the "Limit" setting for this panel to show additional bars.';

      setHiddenBarsNotice({
        type: 'warning',
        message: `${hiddenBarsCount} additional bars not shown.${isUserEditable ? additionalEditMessage : ''}`,
      });
    } else {
      deleteHiddenBarsNotice();
    }

    return () => {
      deleteHiddenBarsNotice();
    };
  }, [appInteractionLevel, deleteHiddenBarsNotice, hiddenBarsCount, setHiddenBarsNotice]);

  const maxValue = mode === 'percentage' ? 100 : dataMax;

  const { compareMode } = useCompareMode();

  const panelKey = usePanelKey();

  const resolvedComparisonLabel =
    comparisonPeriodLabel ?? CompareOptions.find((option) => option.key === compareMode)?.label;

  let chartHeight = 0;
  if (contentDimensions) {
    chartHeight = showModeToggle ? contentDimensions.height - MODE_TOGGLE_HEIGHT : contentDimensions.height;
  }

  const chartProps = {
    width: contentDimensions?.width ?? 0,
    height: chartHeight,
    data: barChartData,
    panelKey,
    maxValue,
    unit,
    mode,
    overrides,
    thresholds,
    isCompareMode,
    comparisonPeriodLabel: resolvedComparisonLabel,
  };

  if (queryResults[0]?.error) throw queryResults[0]?.error;

  if (contentDimensions === undefined) return null;

  return (
    <>
      {showModeToggle && <ModeToggle mode={mode} onChangeMode={onModeChange} />}
      <TopListBase {...chartProps} primaryColor={colorGenerator.palette.Categorical[0]!} onBarClick={onBarClick} />
    </>
  );
}
